import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

export type OnboardingStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';

export interface OnboardingChecklistItem {
  key: string;
  label: string;
  completed: boolean;
  notes?: string;
}

export interface OnboardingAttendee {
  contact_id: string;
  name: string;
  attended: boolean;
}

export interface OnboardingSession {
  id: string;
  item_id: string;
  scheduled_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  conducted_by: string | null;
  attendees: OnboardingAttendee[];
  recording_url: string | null;
  meeting_notes: string | null;
  meeting_url: string | null;
  channel: 'video' | 'voice';
  checklist: OnboardingChecklistItem[];
  client_approved: boolean;
  client_approved_at: string | null;
  status: OnboardingStatus;
  created_at: string;
  created_by: string | null;
}

export interface OnboardingSessionInsert {
  item_id: string;
  scheduled_at?: string | null;
  attendees?: OnboardingAttendee[];
  checklist?: OnboardingChecklistItem[];
  meeting_url?: string | null;
  channel?: 'video' | 'voice';
}

const DEFAULT_CHECKLIST: OnboardingChecklistItem[] = [
  { key: 'contract_review', label: 'Revisar contrato e termos', completed: false },
  { key: 'people_review', label: 'Revisar pessoas no processo', completed: false },
  { key: 'eligibility_review', label: 'Revisar elegibilidade', completed: false },
  { key: 'timeline_explained', label: 'Explicar timeline do processo', completed: false },
  { key: 'documents_requested', label: 'Solicitar documentos necessários', completed: false },
  { key: 'expectations_set', label: 'Alinhar expectativas', completed: false },
  { key: 'questions_answered', label: 'Responder dúvidas do cliente', completed: false },
];

export function useOnboardingSessions(itemId?: string, catalogId?: string) {
  const queryClient = useQueryClient();

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['onboarding-sessions', itemId],
    queryFn: async () => {
      if (!itemId) return [];
      
      const { data, error } = await supabase
        .from('onboarding_sessions')
        .select('*')
        .eq('item_id', itemId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Parse JSON fields and merge checklist with defaults (for existing sessions)
      return (data || []).map((session) => {
        const existingChecklist = (session.checklist as unknown as OnboardingChecklistItem[]) || [];
        
        // Merge: keep existing items state, add missing items from DEFAULT_CHECKLIST
        const mergedChecklist = DEFAULT_CHECKLIST.map(defaultItem => {
          const existing = existingChecklist.find(item => item.key === defaultItem.key);
          return existing || { ...defaultItem, completed: false };
        });
        
        return {
          ...session,
          attendees: (session.attendees as unknown as OnboardingAttendee[]) || [],
          checklist: mergedChecklist,
          channel: (session.channel || 'video') as 'video' | 'voice',
        };
      }) as OnboardingSession[];
    },
    enabled: !!itemId,
  });

  const createSession = useMutation({
    mutationFn: async (data: Partial<OnboardingSessionInsert>) => {
      // Buscar checklist customizado do catálogo se disponível
      let checklistToUse = DEFAULT_CHECKLIST;
      
      if (catalogId) {
        const { data: catalog } = await supabase
          .from('catalog')
          .select('onboarding_checklist')
          .eq('id', catalogId)
          .single();
        
        const catalogChecklist = catalog?.onboarding_checklist as unknown as Array<{key: string; label: string; required?: boolean}>;
        if (catalogChecklist && catalogChecklist.length > 0) {
          checklistToUse = catalogChecklist.map(item => ({
            key: item.key,
            label: item.label,
            completed: false,
          }));
        }
      }
      
      const { data: result, error } = await supabase
        .from('onboarding_sessions')
        .insert({ 
          item_id: itemId!,
          checklist: checklistToUse as unknown as Json,
          attendees: (data.attendees || []) as unknown as Json,
          scheduled_at: data.scheduled_at,
          meeting_url: data.meeting_url || null,
          channel: data.channel || 'video',
        })
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions', itemId] });
      toast.success('Sessão de onboarding agendada');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao agendar sessão');
      console.error(error);
    },
  });

  const updateSession = useMutation({
    mutationFn: async ({ id, checklist, attendees, ...updates }: { id: string } & Partial<OnboardingSession>) => {
      const updateData: Record<string, unknown> = { ...updates };
      if (checklist) updateData.checklist = checklist as unknown as Json;
      if (attendees) updateData.attendees = attendees as unknown as Json;
      
      const { data: result, error } = await supabase
        .from('onboarding_sessions')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions', itemId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao atualizar sessão');
      console.error(error);
    },
  });

  const startSession = useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase
        .from('onboarding_sessions')
        .update({
          status: 'in_progress',
          started_at: new Date().toISOString(),
        })
        .eq('id', sessionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions', itemId] });
      toast.success('Sessão iniciada');
    },
  });

  const completeSession = useMutation({
    mutationFn: async ({ sessionId, checklist, meeting_notes }: { 
      sessionId: string; 
      checklist: OnboardingChecklistItem[];
      meeting_notes?: string;
    }) => {
      // Verificar se todos os itens obrigatórios foram concluídos
      const allCompleted = checklist.every(item => item.completed);
      
      if (!allCompleted) {
        throw new Error('Todos os itens do checklist devem ser concluídos');
      }

      const { error } = await supabase
        .from('onboarding_sessions')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          checklist: checklist as unknown as Json,
          meeting_notes,
        })
        .eq('id', sessionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions', itemId] });
      toast.success('Sessão concluída com sucesso');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao concluir sessão');
    },
  });

  const approveAndFinalize = useMutation({
    mutationFn: async (sessionId: string) => {
      // 1. Marcar sessão como aprovada
      await supabase
        .from('onboarding_sessions')
        .update({
          client_approved: true,
          client_approved_at: new Date().toISOString(),
        })
        .eq('id', sessionId);

      // 2. Atualizar item para 'onboarded' (trigger criará o case)
      // Note: O status 'onboarded' pode não estar no enum TypeScript ainda
      // mas foi adicionado via migração
      const { error } = await supabase
        .from('items')
        .update({ status: 'onboarded' as any })
        .eq('id', itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-sessions', itemId] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      toast.success('Onboarding finalizado! Case criado automaticamente.');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao finalizar onboarding');
    },
  });

  const activeSession = sessions.find(s => s.status === 'scheduled' || s.status === 'in_progress');
  const completedSession = sessions.find(s => s.status === 'completed' && s.client_approved);

  return {
    sessions,
    isLoading,
    activeSession,
    completedSession,
    createSession: createSession.mutateAsync,
    updateSession: updateSession.mutateAsync,
    startSession: startSession.mutateAsync,
    completeSession: completeSession.mutateAsync,
    approveAndFinalize: approveAndFinalize.mutateAsync,
    isCreating: createSession.isPending,
    isUpdating: updateSession.isPending,
  };
}
